/* Global page slices from native, disjoint product-flag streams. */
(function (root) {
  'use strict';
  function plan(yes, no, page, size) {
    if (![yes, no, page, size].every(Number.isSafeInteger) || yes < 0 || no < 0 || page < 1 || size < 1) throw new Error('Invalid pagination state');
    return planGroups([{ available: true, count: yes }, { available: false, count: no }], page, size);
  }
  function planGroups(groups, page, size) {
    if (![page, size].every(Number.isSafeInteger) || page < 1 || size < 1 || groups.some(function (group) { return !Number.isSafeInteger(group.count) || group.count < 0; })) throw new Error('Invalid pagination state');
    var start = (page - 1) * size, stop = start + size, base = 0, parts = [];
    groups.forEach(function (group, index) {
      var offset = Math.max(start, base) - base;
      var end = Math.min(stop, base + group.count) - base;
      while (offset < end) {
        var within = offset % 36;
        var part = { available: group.available, page: Math.floor(offset / 36) + 1, offset: within, take: Math.min(end - offset, 36 - within) };
        if (group.priority !== undefined) { part.priority = group.priority; part.group = index; }
        parts.push(part);
        offset += part.take;
      }
      base += group.count;
    });
    return parts;
  }
  if (typeof module !== 'undefined' && module.exports) { module.exports = { plan: plan, planGroups: planGroups }; return; }

  async function prepare(listing, address, parentSignal) {
    var metadata = listing && listing.querySelector('[data-market-stock-data]');
    if (!metadata) return;
    var list = listing.querySelector('[data-block-id="ku_landing_result_items"] .kuResults > ul');
    var url = new URL(address, root.location.href);
    var country = listing.getAttribute('data-native-country');
    var key = metadata.dataset.key;
    var size = Number(metadata.dataset.size);
    var yes = Number(metadata.dataset.yes), no = Number(metadata.dataset.no);
    var page = Number(url.searchParams.get('page') || 1);
    var controller = new AbortController();
    var cancel = function () { controller.abort(); };
    if (parentSignal) {
      if (parentSignal.aborted) cancel();
      else parentSignal.addEventListener('abort', cancel, { once: true });
    }
    var timer = root.setTimeout(cancel, 10000);
    var requests = new Map();
    var start = performance.now();
    var requestCount = 0;
    var responseBytes = 0;
    function currentStockMismatches(items) {
      return items.filter(function (item) {
        if (!['true', 'false'].includes(item.dataset.stockCurrent)) throw new Error('Current sellability data missing');
        return item.dataset.stockCurrent !== item.dataset.stockFlag;
      }).length;
    }
    var priorityParam = 'filter.p.m.merchandising.priority';
    var groups, boostEnabled = false;
    async function stream(available, streamPage, priority, expectedCount) {
      var cacheKey = available + ':' + streamPage + ':' + priority;
      if (requests.has(cacheKey)) return requests.get(cacheKey);
      var operation = (async function () {
        var target = new URL(url);
        target.searchParams.delete('sections');
        target.searchParams.set('section_id', 'market-stock-stream');
        target.searchParams.set('country', country);
        target.searchParams.set('page', String(streamPage));
        target.searchParams.set('filter.p.m.inventory.' + key, available ? '1' : '0');
        if (priority !== undefined) target.searchParams.set(priorityParam, String(priority));
        requestCount++;
        var response = await fetch(target, { signal: controller.signal });
        if (!response.ok) throw new Error('Stream HTTP ' + response.status);
        var html = await response.text();
        responseBytes += new TextEncoder().encode(html).byteLength;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var source = doc.querySelector('[data-market-stock-stream]');
        if (!source || source.dataset.country !== country || source.dataset.key !== key || Number(source.dataset.page) !== streamPage) throw new Error('Wrong stream context');
        var count = Number(source.dataset.count);
        var items = Array.from(source.querySelector('ul').children);
        if (!Number.isSafeInteger(count) || count < 0) throw new Error('Invalid stream count');
        if (expectedCount === undefined) expectedCount = available ? yes : no;
        if (count !== expectedCount) throw new Error('Dataset changed; refresh');
        items.forEach(function (item) {
          if (item.dataset.stockFlag !== String(available)) throw new Error('Sellability filter index is stale');
          if (priority !== undefined && item.dataset.priority !== String(priority)) throw new Error('Priority filter index is stale');
        });
        var expectedItems = Math.max(0, Math.min(36, count - (streamPage - 1) * 36));
        if (items.length !== expectedItems && !(streamPage === 1 && count <= 36)) throw new Error('Native index has a pagination gap');
        return { count: count, items: items };
      })();
      requests.set(cacheKey, operation);
      return operation;
    }
    try {
      if (!['US', 'CA'].includes(country) || key !== (country === 'CA' ? 'ca_sellable' : 'us_sellable')) throw new Error('Unsupported market');
      if (url.searchParams.has('filter.p.m.inventory.us_sellable') || url.searchParams.has('filter.p.m.inventory.ca_sellable')) { listing.dataset.marketStockState = 'fallback'; return; }
      if (Number(metadata.dataset.total) === 0) { listing.dataset.marketStockState = 'sorted'; return; }
      if (!list || yes < 0 || no < 0 || yes + no !== Number(metadata.dataset.total)) throw new Error('Flags are not indexed for every result');
      var sort = url.searchParams.get('sort_by') || metadata.dataset.sort;
      var priorityAllowed = ['manual', 'best-selling', 'most-relevant', 'relevance'].includes(sort);
      var tiers = [2, 1, 0].map(function (priority) { return { priority: priority, count: Number(metadata.dataset['priority' + priority]) }; });
      // Missing integers are not zero in Shopify filtering. Require full coverage.
      var priorityComplete = tiers.every(function (tier) { return Number.isSafeInteger(tier.count) && tier.count >= 0; }) && tiers.reduce(function (sum, tier) { return sum + tier.count; }, 0) === yes + no;
      listing.dataset.marketBoostState = !priorityAllowed ? 'sort-disabled' : priorityComplete ? 'uniform' : 'not-indexed';
      if (priorityAllowed && url.searchParams.has(priorityParam)) {
        listing.dataset.marketBoostState = 'explicit-filter';
      } else if (priorityAllowed && priorityComplete) {
        var populated = tiers.filter(function (tier) { return tier.count > 0; });
        if (populated.length > 1) {
          // Independent count-only queries run in parallel; derive the last tier
          // from the stock totals to avoid an unnecessary third count request.
          var counted = await Promise.all(populated.slice(0, -1).map(async function (tier) {
            var target = new URL(url);
            target.searchParams.delete('sections');
            target.searchParams.set('section_id', 'market-stock-stream');
            target.searchParams.set('country', country);
            target.searchParams.set('page', '1');
            target.searchParams.set(priorityParam, String(tier.priority));
            requestCount++;
            var countResponse = await fetch(target, { signal: controller.signal });
            if (!countResponse.ok) throw new Error('Priority count HTTP ' + countResponse.status);
            var countHTML = await countResponse.text();
            responseBytes += new TextEncoder().encode(countHTML).byteLength;
            var countData = new DOMParser().parseFromString(countHTML, 'text/html').querySelector('[data-market-stock-stream]');
            if (!countData || countData.dataset.country !== country || countData.dataset.key !== key || countData.dataset.countOnly !== 'true' || Number(countData.dataset.page) !== 1 || Number(countData.dataset.count) !== tier.count) throw new Error('Wrong priority count context');
            var tierYes = Number(countData.dataset.yes), tierNo = Number(countData.dataset.no);
            if (![tierYes, tierNo].every(Number.isSafeInteger) || tierYes < 0 || tierNo < 0 || tierYes > yes || tierNo > no || tierYes + tierNo !== tier.count) throw new Error('Inconsistent priority counts');
            var visibleYes = tierYes, visibleNo = tierNo;
            if (tier.count <= 36) {
              visibleYes = Number(countData.dataset.visibleYes); visibleNo = Number(countData.dataset.visibleNo);
              if (![visibleYes, visibleNo].every(Number.isSafeInteger) || visibleYes < 0 || visibleNo < 0 || visibleYes > tierYes || visibleNo > tierNo) throw new Error('Invalid visible priority counts');
            }
            return { priority: tier.priority, yes: tierYes, no: tierNo, visibleYes: visibleYes, visibleNo: visibleNo };
          }));
          var last = populated[populated.length - 1];
          var lastYes = yes - counted.reduce(function (sum, tier) { return sum + tier.yes; }, 0);
          var lastNo = no - counted.reduce(function (sum, tier) { return sum + tier.no; }, 0);
          if (lastYes < 0 || lastNo < 0 || lastYes + lastNo !== last.count) throw new Error('Inconsistent remaining priority counts');
          counted.push({ priority: last.priority, yes: lastYes, no: lastNo, visibleYes: lastYes, visibleNo: lastNo });
          groups = [true, false].flatMap(function (available) { return counted.map(function (tier) { return { available: available, priority: tier.priority, count: available ? tier.visibleYes : tier.visibleNo, indexedCount: available ? tier.yes : tier.no }; }); });
          boostEnabled = true;
          listing.dataset.marketBoostState = 'sorted';
        }
      }
      var nativeItems = Array.from(list.children);
      if (!boostEnabled && (yes === 0 || no === 0)) {
        var onlyFlag = String(no === 0);
        if (nativeItems.some(function (item) { return item.dataset.stockFlag !== onlyFlag; })) throw new Error('Sellability filter index is stale');
        var nativeMismatches = currentStockMismatches(nativeItems);
        listing.dataset.marketStockState = 'sorted';
        delete listing.dataset.marketStockError;
        listing.dataset.marketStockMetrics = JSON.stringify({ requests: 0, elapsedMs: Math.round(performance.now() - start), page: page, yes: yes, no: no, currentStockMismatches: nativeMismatches });
        return;
      }
      if (!groups) groups = [{ available: true, count: yes }, { available: false, count: no }];
      function expected(part) { var group = groups[part.group === undefined ? (part.available ? 0 : 1) : part.group]; return group.indexedCount === undefined ? group.count : group.indexedCount; }
      function slices() { return planGroups(groups, page, size); }
      var parts = slices();
      await Promise.all(parts.map(function (part) { return stream(part.available, part.page, part.priority, expected(part)); }));
      // Correct only complete short streams for known market exclusions.
      for (var index = 0; index < groups.length; index++) {
        var group = groups[index];
        var response = requests.get(group.available + ':1:' + group.priority);
        if (response && group.count <= 36) group.count = (await response).items.length;
      }
      yes = groups.filter(function (group) { return group.available; }).reduce(function (sum, group) { return sum + group.count; }, 0);
      no = groups.filter(function (group) { return !group.available; }).reduce(function (sum, group) { return sum + group.count; }, 0);
      parts = slices();
      var chunks = await Promise.all(parts.map(async function (part) {
        // Counts were validated against the original indexed group count above;
        // cached responses can have fewer cards only for a complete short stream.
        var result = await stream(part.available, part.page, part.priority, expected(part));
        return result.items.slice(part.offset, part.offset + part.take);
      }));
      if (controller.signal.aborted) throw new DOMException('Cancelled', 'AbortError');
      var items = chunks.flat();
      if (new Set(items.map(function (item) { return item.dataset.id; })).size !== items.length) throw new Error('Duplicate products');
      // Stock can change before Flow updates the product flags. Preserve the
      // indexed partition and page offsets; the cards retain live sellability.
      var mismatches = currentStockMismatches(items);
      var center = listing.querySelector('[data-container-role="center"]');
      if (!center) throw new Error('Native grid container missing');
      list.replaceChildren(...items);
      var total = yes + no;
      var heading = listing.querySelector('.kuResultsHeadingTitle');
      if (heading) heading.textContent = total + (total === 1 ? ' product' : ' products');
      var footer = listing.querySelector('[data-block-id="ku_landing_result_footer"]');
      if (!footer) {
        footer = document.createElement('footer'); footer.dataset.blockId = 'ku_landing_result_footer';
        center.appendChild(footer);
      }
      footer.replaceChildren();
      var pages = Math.ceil(total / size);
      if (pages > 1) {
        var pagination = document.createElement('nav'); pagination.className = 'pagination numerical-pagination';
        pagination.setAttribute('aria-label', 'Collection pagination');
        function link(number, label, relation) {
          var target = new URL(url); target.searchParams.set('page', number);
          var anchor = document.createElement('a'); anchor.href = target.href; anchor.textContent = label;
          if (relation) anchor.rel = relation;
          pagination.appendChild(anchor);
        }
        if (page > 1) link(page - 1, 'Previous', 'prev');
        var current = document.createElement('span'); current.textContent = ' Page ' + page + ' of ' + pages + ' '; current.setAttribute('aria-current', 'page'); pagination.appendChild(current);
        if (page < pages) link(page + 1, 'Next', 'next');
        footer.appendChild(pagination);
      }
      listing.dataset.marketStockState = 'sorted';
      delete listing.dataset.marketStockError;
      listing.dataset.marketStockMetrics = JSON.stringify({ requests: requestCount, responseBytes: responseBytes, elapsedMs: Math.round(performance.now() - start), page: page, yes: yes, no: no, currentStockMismatches: mismatches });
      if (root.initializeProductCardSwipers) root.initializeProductCardSwipers(listing);
    } catch (error) {
      if (parentSignal && parentSignal.aborted) throw error;
      listing.dataset.marketStockState = 'fallback';
      listing.dataset.marketBoostState = 'fallback';
      listing.dataset.marketStockError = error.message;
      console.warn('Market stock preview kept native ordering:', error.message);
    } finally {
      root.clearTimeout(timer);
      if (parentSignal) parentSignal.removeEventListener('abort', cancel);
    }
  }
  root.MarketStockSort = { prepare: prepare, plan: plan };
})(typeof window === 'undefined' ? globalThis : window);
